"use client";

import NextImage, { type ImageProps as NextImageProps } from "next/image";
import NextLink, { type LinkProps as NextLinkProps } from "next/link";
import { useLocale, useTranslations } from "next-intl";

import {
	OfferCardShowcase,
	type OfferCardShowcaseProps
} from "@/composite/OfferCardShowcase";
import type { AddItemsToHybrisCartRequest } from "@/infrastructure/hybris";

import { SessionState, useSessionStore } from "@/app/store";
import type { OfferReference, ProductReference } from "@/types/references";
import { resolveImageProps } from "@/app/utilities/images";
import { mapTagColor } from "@/app/utilities/mapTagColor";

import type { OfferCardShowcaseBlockProps } from ".";
import { ProductsResponse } from "@/domain/product";

export interface OfferCardShowcaseBlockClientProps extends OfferCardShowcaseBlockProps {
	offerData?: OfferReference;
	productsData: ProductReference[];
	productsResponse: ProductsResponse;
	country: AddItemsToHybrisCartRequest["country"];
}

/**
 * Featured Card block component
 */
export function OfferCardShowcaseBlock({
	headline,
	description,
	button,
	offerData,
	productsData,
	productsResponse,
	theme,
	country
}: OfferCardShowcaseBlockClientProps): JSX.Element {
	if (productsResponse?.type === "failure") {
		throw new Error(productsResponse.message);
	}

	const locale = useLocale();
	const t = useTranslations("featuredOfferCard");
	const tCart = useTranslations("cart");
	const [tagColor, tagLabel] = offerData?.offer_type?.split("|") ?? [
		"purple",
		""
	];
	const cardDescription = offerData?.description ?? "";
	const modalData = offerData?.details_and_condition_modal;
	const formattedDeadline =
		offerData?.deadline &&
		new Date(offerData?.deadline).toLocaleString(locale, {
			month: "long",
			day: "2-digit"
		});

	const sessionState = useSessionStore(
		(state) => state.sessionState
	) as SessionState;

	return (
		<OfferCardShowcase<NextImageProps, NextLinkProps>
			audienceTag={
				tagLabel
					? {
							kind: "solid",
							size: "sm",
							color: mapTagColor(tagColor) as NonNullable<
								OfferCardShowcaseProps["audienceTag"]
							>["color"],
							label: tagLabel
						}
					: undefined
			}
			cardTitle={{
				tag: headline.headline_tag,
				children: headline.headline
			}}
			cardDescription={cardDescription}
			badgeText={
				formattedDeadline &&
				t("badgeDeadline", { deadline: formattedDeadline })
			}
			badgeCtaLabel={modalData?.modal_title.headline}
			badgeCtaModal={{
				title: modalData?.modal_title.headline ?? "",
				titleTag: modalData?.modal_title.headline_tag,
				description: modalData?.description ?? ""
			}}
			ctas={[
				{
					component: NextLink,
					href: button.link.href,
					children: button.link.title
				}
			]}
			products={
				productsData?.map((product) => {
					const productData = productsResponse?.products?.find(
						({ sku }) => sku === product.sku
					);

					return {
						image: {
							...resolveImageProps("1:1"),
							component: NextImage,
							src: productData?.image ?? "",
							alt: productData?.name ?? ""
						},
						title: {
							children: product?.title ?? "",
							href: product?.meta_data?.canonical_url?.href ?? ""
						},
						rating: {
							rating: productData?.starsRating ?? 0,
							label: productData?.numRatings
								? `(${productData?.numRatings})`
								: ""
						},
						price: {
							display: productData?.salePrice ?? 0
						},
						cta: {
							children: tCart("addToCart")
						},
						info: {
							itemNumber: productData?.sku ?? "",
							country: country ?? "",
							purchaseType: "oneTime"
						}
					};
				}) ?? []
			}
			theme={
				(theme === "Product Showcase Carousel"
					? "slider"
					: "grid") as OfferCardShowcaseProps["theme"]
			}
			alreadyAddedMessage={tCart("alreadyAddedMessage")}
			errorMessage={tCart("errorMessage")}
		/>
	);
}
